import { AGENT_DISPLAY_NAMES } from "@/lib/agent-ids";
import { CSV_BOM, csvRow } from "@/lib/csv";
import type { CostSummary } from "@/types/api";

/**
 * Export CSV da tela `/cost` (#429/SIMP.8).
 *
 * Delimitador, escape e BOM vêm de `lib/csv.ts` — a regra é a mesma do CSV de
 * reference data, e duas cópias dela divergiriam.
 *
 * **Cabeçalhos traduzidos**, ao contrário do CSV de ingestão: este arquivo é
 * lido por pessoas numa planilha, não reprocessado por parser nosso. Um
 * cabeçalho em inglês num app apresentado em pt-BR seria a única parte do
 * produto que não acompanha o idioma de quem usa. (Se um dia alguém precisar
 * de schema estável para automação, o caminho é um endpoint próprio, não
 * congelar o idioma deste export.)
 *
 * Números saem crus (`String(n)`), nunca por `compactUsd`/`detailedTokens` —
 * esses abreviam e prefixam `$` para leitura na tela; um CSV existe para ser
 * somado numa planilha, e arredondar aqui destruiria precisão que ninguém
 * pediu para perder.
 */

/** Assinatura mínima de `t` — evita arrastar o tipo do i18next até aqui. */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Serializa um `CostSummary` inteiro em três seções — a mesma partição que
 * a tela desenha (By agent / By model / Daily trend). Pura e testável: não
 * toca `document`/`Blob` (isso fica em `downloadCostCsv`). */
export function costCsv(costs: CostSummary, t: TFn): string {
  const lines: string[] = [];

  lines.push(t("SPEND BY AGENT"));
  lines.push(
    csvRow([
      t("Agent"),
      t("Executions"),
      t("Execution cost (USD)"),
      t("Chats"),
      t("Chat cost (USD)"),
      t("Total tokens"),
      t("Total cost (USD)"),
    ]),
  );
  for (const a of costs.by_agent ?? []) {
    lines.push(
      csvRow([
        AGENT_DISPLAY_NAMES[a.agent_type] || a.agent_type,
        a.execution_count,
        a.execution_cost,
        a.chat_count,
        a.chat_cost,
        a.total_tokens,
        a.total_cost,
      ]),
    );
  }
  lines.push("");

  lines.push(t("TOKEN MIX BY MODEL"));
  lines.push(
    csvRow([t("Model"), t("Calls"), t("Total tokens"), t("Total cost (USD)")]),
  );
  for (const m of costs.by_model ?? []) {
    lines.push(csvRow([m.model_label || m.model, m.call_count, m.total_tokens, m.total_cost]));
  }
  lines.push("");

  lines.push(t("DAILY TREND"));
  lines.push(csvRow([t("Date"), t("Total tokens"), t("Total cost (USD)")]));
  for (const d of costs.by_day ?? []) {
    lines.push(csvRow([d.day, d.total_tokens, d.total_cost]));
  }

  return CSV_BOM + lines.join("\n") + "\n";
}

/** Nome de arquivo legível: o do projeto, não o UUID da rota.
 *
 * Sanitizado porque nome de projeto é texto livre do usuário e vai para o
 * sistema de arquivos — `/`, `\` e afins viram `-`. Projeto sem nome (ainda
 * carregando) cai em "cost", que é honesto e não quebra o download. */
export function costCsvFilename(projectName: string | null | undefined, day: string): string {
  const safe = (projectName ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return `${safe || "cost"}-${day}.csv`;
}

/** Dispara o download no navegador — mesmo mecanismo de
 * `_downloadBomBlob` em `api/agents.ts` (Blob + link temporário), só que sem
 * round-trip ao backend: os três recortes já chegaram ao cliente na mesma
 * chamada que desenha a tela. */
export function downloadCostCsv(costs: CostSummary, filename: string, t: TFn): void {
  const blob = new Blob([costCsv(costs, t)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
